<template>
  <div
    class="chips__container"
    :class="{ 'chips__container--active': filtersStore.isAnyFilterActive.value }"
  >
    <!-- Reset all -->
    <div
      v-show="filtersStore.isAnyFilterActive.value"
      class="resetContainer"
    >
      <FiltersChip
        id="resetChip"
        :title="$l('showresult.filter.resetTitle')"
        :class="{ 'compact': !showResetTitle }"
        icon="icon-cross"
        @click="filtersStore.resetAll"
      />
    </div>
    <div
      ref="filtersContainer"
      class="filtersContainer"
      tabindex="-1"
    >
      <FiltersChip
        v-for="filter in filtersSortedByActive"
        :id="filter.component + '--chip'"
        :key="filter.title"
        :title="filter.title"
        :icon="filter.icon"
        :active="filtersStore.isFilterActive(filter.propertyKeys) && !modalIsOpen"
        @click="filter?.click ? filter.click(filter) : modalOpen(filter)"
      />
    </div>
    <Teleport to="body">
      <FiltersModal
        v-if="modalIsOpen"
        :selected-filter="selectedFilterComponent"
        :property-keys="selectedFilterComponent.propertyKeys"
        @close="modalClose"
        @reset="filtersStore.reset(selectedFilterComponent.propertyKeys)"
      />
    </Teleport>
  </div>
</template>

<script setup>
import { classList, gebi, l, qs, qsa, tabTrappingHelper } from '@utils/toolbox.js';
import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';
const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();


const modalIsOpen = ref(false);
const selectedFilterComponent = ref({});
const filtersContainer = ref(null);
const showResetTitle = ref(true);


const filters = computed(() => {
	return [
		{
			component: 'FiltersItemTransfers',
			title: l('showresult.filter.transfersTitle'),
			icon: 'icon-transfer',
			propertyKeys: ['numStops', 'changesAirport', 'airportWaitTime'],
			isVisible: !(resultQueryStore.state.response.uniqueNumStops.includes(0) && resultQueryStore.state.response.uniqueNumStops.length === 1)
		},
		{
			component: 'FiltersItemTime',
			title: l('showresult.filter.timeTitle'),
			icon: 'icon-circle-time',
			propertyKeys: ['traveltime', 'departureTime', 'arrivalTime'],
			isVisible: true
		},
		{
			component: 'FiltersItemBaggage',
			title: l('filters.baggage.chipName'),
			icon: 'icon-person-luggage',
			propertyKeys: ['baggageIncluded', 'cabinBaggageIncluded'],
			isVisible: true
		},
		{
			component: 'FiltersItemFlightClass',
			title: l('global.simplePhrases.rebooking'),
			icon: 'icon-loop',
			propertyKeys: ['flexTicket'],
			click () {
				filtersStore.set({ key: 'flexTicket', value: filtersStore.state.flexTicket ? null : true });
			},
			isVisible: true
		},
		{
			component: 'FiltersItemPriceMobile',
			title: l('showresult.filter.priceTitle'),
			icon: 'icon-partCircle-euro',
			propertyKeys: ['price', 'youthTicket', 'paymentMethodIncluded'],
			isVisible: true
		},
		{
			component: 'FiltersItemAirlines',
			title: l('showresult.filter.airlinesTitle'),
			icon: 'icon-plane-tail',
			propertyKeys: ['enabledAirlines', 'blacklisted', 'alliances'],
			isVisible: true
		},
		{
			component: 'FiltersItemAirports',
			title: l('showresult.filter.airportsTitle'),
			icon: 'icon-signalTower-planeUp',
			propertyKeys: ['airportDepartures', 'airportArrivals', 'airportStops'],
			isVisible: true
		},
		{
			component: 'FiltersItemAgencies',
			title: l('showresult.filter.travelAgencyFilterTitle'),
			icon: 'icon-circle-tree',
			propertyKeys: ['enabledAgents', 'foreign'],
			isVisible: true
		},
		{
			component: 'FiltersItemCarbonCompensate',
			title: l('global.simplePhrases.climateCompensation'),
			icon: 'icon-leaves-outline',
			propertyKeys: ['isSortByLowestEmissionsEnabled', 'climateCompensated'],
			isVisible: true
		}
	].filter(({ isVisible }) => isVisible);
});


// If no filter is active, scroll to the left
watch(filtersStore.isAnyFilterActive, (newValue) => {
	if (!newValue) {
		filtersContainer.value.scrollLeft = 0;
	}
});

// Watch for changes in the filter object, scroll to the last activated filter
watch(() => filtersStore.meta.lastKeyUpdated, async (newValue) => {
	// Check if the filter is active
	if (!newValue) {
		return;
	}

	// If only one filter has been applied, scroll to the left
	if (filtersStore.activeFilters.value.length === 1) {
		filtersContainer.value.scrollLeft = 0;
		return;
	}

	await nextTick();

	// Find the index of the filter with the matching key
	const filterIndex = filtersSortedByActive.value.find(filter => filter.propertyKeys.includes(newValue));

	// Find the chip element we want to scroll to
	const filterElement = document.getElementById(`${filterIndex.component}--chip`);


	// Scroll into view horizontally only in the center of the container
	if (filterElement) {
		const containerWidth = filtersContainer.value.clientWidth;
		const elementWidth = filterElement.clientWidth;
		const scrollLeft = filterElement.offsetLeft - (containerWidth / 2) + (elementWidth / 2);
		filtersContainer.value.scrollTo({ left: scrollLeft, behavior: 'instant' });
	}
}, { deep: true });

async function modalOpen (filter) {
	selectedFilterComponent.value = filter;
	modalIsOpen.value = true;
	classList('body').add('modal-open');
	if (document.body.classList.contains('tabmode')) {
		await nextTick();
		const modal = qs('.filtersModal');

		tabTrappingHelper(modal, filter.title, modalClose, 'filtersModal--visible', qsa('a, button, .abBtnCard, input:not([type="checkbox"]), .checkbox, textarea, select, .noUi-handle, [role="button"], .abToggleSwitch', modal));
	}
}

function modalClose () {
	modalIsOpen.value = false;
	classList('body').remove('modal-open');

	if (document.body.classList.contains('tabmode')) {
		gebi('filters-mobile').focus();
	}
}

const filtersSortedByActive = computed(() => {
	// Create an array of objects with filter, its active state, and original index
	return filters.value
		.map((filter, index) => ({
			filter,
			isActive: filtersStore.isFilterActive(filter.propertyKeys),
			index
		}))
		.sort((a, b) => {
			// Active filters come first
			if (a.isActive && !b.isActive) {
				return -1;
			}
			if (!a.isActive && b.isActive) {
				return 1;
			}
			// Maintain original order for filters in the same active group
			return a.index - b.index;
		})
	// Extract the filters from our metadata objects
		.map(item => item.filter);
});

onMounted(() => {
	filtersContainer.value.addEventListener('scroll', () => {
		if (!filtersStore.isAnyFilterActive.value) {
			return;
		}

		const shouldShow = filtersContainer.value.scrollLeft === 0;

		if (shouldShow !== showResetTitle.value) {
			showResetTitle.value = shouldShow;
		}
	});
});

</script>
